import { ColumnDef } from '@tanstack/react-table'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import { RolodexDocumentEntry } from 'features/files/types'
import { getFileTypeIcon } from 'features/files/utils/DocumentUtils'
import { InputType } from 'types'

import { GenericFileActionsCell } from './GenericFileActionsCell'

export interface GenericFileTableColumnsOptions {
  showDownload?: boolean
  showDelete?: boolean
  forceRefresh?: () => Promise<void> | void
}

const getDocumentName = (document: RolodexDocumentEntry): string =>
  document.visible_name || document.filename || 'Unnamed file'

export const createGenericFileTableColumns = (
  options: GenericFileTableColumnsOptions = {}
): ReadonlyArray<ColumnDef<RolodexDocumentEntry>> => {
  const { showDownload = true, showDelete = true, forceRefresh } = options

  const columns: ColumnDef<RolodexDocumentEntry>[] = [
    {
      accessorKey: 'file_type',
      id: 'file_type',
      header: 'File Type',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => {
        const fileType = row.original.file_type || 'unknown'
        return (
          <Tag icon={getFileTypeIcon(row.original)}>
            {fileType.toUpperCase()}
          </Tag>
        )
      },
    },
    {
      accessorFn: getDocumentName,
      id: 'file_name',
      header: 'File Name',
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Text,
      },
    },
    {
      accessorKey: 'upload_date',
      id: 'upload_date',
      header: 'Upload Date',
      sortingFn: 'datetime',
      sortUndefined: 'last',
      filterFn: 'dateRangeFilter',
      meta: {
        inputType: InputType.Date,
      },
      cell: ({ row }) => {
        const uploadDate = row.original.upload_date
        return uploadDate ? dayjs(uploadDate).format('MMM D, YYYY') : 'N/A'
      },
    },
    {
      accessorKey: 'categories',
      id: 'categories',
      header: 'Categories',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => {
        const categories = row.original.categories
        if (categories.length === 0) return 'Uncategorized'

        return (
          <>
            {categories.map((category) => (
              <Tag key={category}>{category}</Tag>
            ))}
          </>
        )
      },
    },
  ]

  if (showDownload || showDelete) {
    columns.push({
      accessorKey: 'actions',
      id: 'actions',
      header: '',
      enableGlobalFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <GenericFileActionsCell
          downloadEnabled={showDownload}
          deleteEnabled={showDelete}
          document={row.original}
          forceRefresh={forceRefresh}
        />
      ),
    })
  }

  return columns
}
